import React, { useMemo, useState } from "react";
import { Badge, Form } from "react-bootstrap";
import { FaSearch, FaUsers, FaTimes } from "react-icons/fa";
import "./EmployeMultiPicker.css";

const getInitiales = (nom = "", prenom = "") => {
  const n = (nom || "").trim().charAt(0).toUpperCase();
  const p = (prenom || "").trim().charAt(0).toUpperCase();
  return `${p}${n}` || "?";
};

const EmployeMultiPicker = ({
  options = [],
  selected = [],
  onChange,
  disabled = false,
  title = "Sélection des employés",
}) => {
  const [search, setSearch] = useState("");

  const selectedIds = useMemo(
    () => new Set(selected.map((s) => String(s.value))),
    [selected]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      const haystack = [
        opt.label,
        opt.nom,
        opt.prenom,
        opt.poste,
        opt.departement,
        opt.matricule,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [options, search]);

  const toggleOne = (opt) => {
    if (disabled) return;
    const id = String(opt.value);
    if (selectedIds.has(id)) {
      onChange(selected.filter((s) => String(s.value) !== id));
    } else {
      onChange([...selected, opt]);
    }
  };

  const selectAllVisible = () => {
    if (disabled) return;
    const merged = new Map(selected.map((s) => [String(s.value), s]));
    filtered.forEach((opt) => merged.set(String(opt.value), opt));
    onChange(Array.from(merged.values()));
  };

  const clearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  const removeOne = (e, id) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(selected.filter((s) => String(s.value) !== String(id)));
  };

  return (
    <div className="emp-multi-picker">
      <div className="emp-multi-picker-shell">
        <div className="emp-multi-picker-top">
          <div className="emp-multi-picker-title-row">
            <h6 className="emp-multi-picker-title">
              <FaUsers /> {title}
            </h6>
            <Badge className="emp-multi-count-badge">
              {selected.length} sélectionné{selected.length !== 1 ? "s" : ""}
            </Badge>
          </div>
          <div className="emp-multi-search-row">
            <div className="emp-multi-search-wrap">
              <FaSearch />
              <Form.Control
                type="search"
                placeholder="Rechercher par nom, poste, département…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={disabled}
              />
            </div>
            <div className="emp-multi-actions">
              <button
                type="button"
                className="btn emp-multi-btn emp-multi-btn-select"
                onClick={selectAllVisible}
                disabled={disabled || filtered.length === 0}
              >
                Tout sélectionner ({filtered.length})
              </button>
              <button
                type="button"
                className="btn emp-multi-btn emp-multi-btn-clear"
                onClick={clearAll}
                disabled={disabled || selected.length === 0}
              >
                Tout effacer
              </button>
            </div>
          </div>
        </div>

        {selected.length > 0 && (
          <div className="emp-multi-chips-bar">
            {selected.map((opt) => (
              <span key={opt.value} className="emp-multi-chip">
                {opt.label}
                <button
                  type="button"
                  className="emp-multi-chip-remove"
                  onClick={(e) => removeOne(e, opt.value)}
                  disabled={disabled}
                  aria-label={`Retirer ${opt.label}`}
                >
                  <FaTimes />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="emp-multi-grid-wrap">
          {filtered.length === 0 ? (
            <div className="emp-multi-empty">
              <strong>Aucun employé trouvé</strong>
              {search ? `Aucun résultat pour « ${search} »` : "Aucun employé disponible"}
            </div>
          ) : (
            <div className="emp-multi-grid">
              {filtered.map((opt) => {
                const isSelected = selectedIds.has(String(opt.value));
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`emp-multi-card ${isSelected ? "selected" : ""}`}
                    onClick={() => toggleOne(opt)}
                    disabled={disabled}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      onClick={(e) => e.stopPropagation()}
                      tabIndex={-1}
                      aria-hidden
                    />
                    <div className="emp-multi-avatar" aria-hidden="true">
                      {getInitiales(opt.nom, opt.prenom)}
                    </div>
                    <div className="emp-multi-card-body">
                      <div className="emp-multi-name">{opt.label}</div>
                      {opt.poste && (
                        <div className="emp-multi-meta">{opt.poste}</div>
                      )}
                      {opt.departement && (
                        <div className="emp-multi-meta">{opt.departement}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeMultiPicker;
